
import { Component, OnInit, inject, signal, viewChild } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";

import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { PasswordModule } from 'primeng/password';
import { StyleClassModule } from 'primeng/styleclass';

import { jwtDecode } from 'jwt-decode';
import { finalize } from 'rxjs';
import { CaptchaComponent } from '../../../../../common/components/captcha/captcha.component';
import { BRAND_TAGLINE } from '../../../../../common/constants/branding.constant';
import { ComponentAccess } from '../../../../../common/decorators/component-access.decorator';
import { TokenPair } from '../../../../../common/dtos/token-pair.dto';
import { CommonRoute } from '../../../../../common/enums/common-route.enum';
import { LoginQueryParam } from '../../../../../common/enums/login-query-param.enum';
import { CaptchaMessage } from '../../../../../common/enums/captcha-message.enum';
import { JwtPayload } from '../../../../../common/resources/jwt-payload.resource';
import { AuthenticationService } from '../../../../../common/services/authentication/authentication.service';
import { ReturnUrlService } from '../../../../../common/services/return-url/return-url.service';
import { TokenStorageService } from '../../../../../common/services/token-storage/token-storage.service';
import { LayoutService } from '../../../../layout/service/app.layout.service';
import { ThemeService } from '../../../../theme/theme.service';
import { AppConfigService } from "../../../../../common/config/app.config.service";

@Component({
  selector: "app-login",
  standalone: true,
  templateUrl: "./login.component.html",
  imports: [
    StyleClassModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    InputTextModule,
    PasswordModule,
    CaptchaComponent,
    CardModule
],
})
@ComponentAccess("LoginComponent")
export class LoginComponent implements OnInit {
  readonly layoutService = inject(LayoutService);
  private readonly fb = inject(FormBuilder);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly route = inject(ActivatedRoute);
  private readonly returnUrlService = inject(ReturnUrlService);
  private readonly configService = inject(AppConfigService);
  readonly themeService = inject(ThemeService);
  private readonly tokenStorage = inject(TokenStorageService);

  protected readonly tagline = BRAND_TAGLINE;
  loginForm: FormGroup;
  mfaForm: FormGroup;
  // Display-state fields are signals so the @if blocks and PrimeNG button
  // disabled/loading bindings re-render coherently under zoneless CD.
  // Using plain fields here caused NG0100 on the MFA verify screen because
  // the @if flip happened mid-pass while p-password was still tearing down.
  readonly loginFailed = signal(false);
  readonly mfaRequired = signal(false);
  readonly isVerifyingMfa = signal(false);
  readonly isSigningIn = signal(false);
  readonly isCaptchaRequired = signal(false);
  // When true the MFA step accepts a one-time backup code instead of a TOTP.
  readonly useBackupCode = signal(false);
  backupCode = '';

  private readonly captcha = viewChild(CaptchaComponent);
  isRegistrationAllowed: boolean = false;

  constructor() {
    this.loginForm = this.fb.group({
      email: ["", [Validators.required, Validators.email]],
      password: ["", Validators.required],
    });

    this.mfaForm = this.fb.group({
      totpCode: [
        "",
        [
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(6),
          Validators.pattern("^[0-9]{6}$"),
        ],
      ],
    });

    // Set registration status based on environment configuration
    this.isRegistrationAllowed =
      this.configService.isRegistrationAllowed;
  }

  ngOnInit() {
    this.isCaptchaRequired.set(this.configService.isCaptchaEnabled);
    if (
      this.tokenStorage.decodeToken(
        this.authenticationService.getAccessToken() || ""
      )?.mfaRequired
    ) {
      this.mfaRequired.set(true);
    }
    // "Lost your authenticator?" sends people with backup codes back here:
    // open the code step on backup-code entry so they need not find the toggle.
    if (this.route.snapshot.queryParamMap.get(LoginQueryParam.UseBackupCode) === 'true') {
      this.useBackupCode.set(true);
    }
  }

  async onSubmit(): Promise<void> {
    if (this.loginForm.valid) {
      // Busy from the first click: the captcha challenge and the login
      // round-trip both happen after submit, which can take a while on a
      // slow connection.
      this.isSigningIn.set(true);

      // The captcha challenge runs on demand here (execute-on-submit), so
      // nothing refreshes or goes stale while the login page sits open.
      const captchaOk = (await this.captcha()?.verify()) ?? false;
      if (this.isCaptchaRequired() && !captchaOk) {
        this.isSigningIn.set(false);
        this.messageService.add({
          severity: "error",
          summary: CaptchaMessage.FailedSummary,
          detail: CaptchaMessage.Failed,
        });
        return;
      }

      const { email, password } = this.loginForm.value;
      this.authenticationService
        .login(email, password)
        .pipe(finalize(() => this.isSigningIn.set(false)))
        .subscribe({
        next: (result: TokenPair) => {
          const decodedToken: JwtPayload = jwtDecode(result.accessToken);
          if (decodedToken.mfaSetupRequired) {
            this.router.navigate(["/mfa-setup"]);
          } else if (decodedToken.mfaRequired) {
            this.mfaRequired.set(true);
          } else {
            this.navigateToReturnUrl();
          }
        },
      });
    } else {
      this.loginForm.markAllAsTouched();
    }
  }

  onVerifyMfa(): void {
    const usingBackup = this.useBackupCode();
    let code: string;
    if (usingBackup) {
      if (!this.backupCode.trim()) {
        this.messageService.add({
          severity: "warn",
          detail: "Please enter a backup code.",
        });
        return;
      }
      code = this.backupCode.trim();
    } else {
      if (this.mfaForm.invalid) {
        this.mfaForm.markAllAsTouched();
        return;
      }
      code = this.mfaForm.value.totpCode;
    }

    this.isVerifyingMfa.set(true);
    this.authenticationService
      .verifyMfaLogin(code, usingBackup)
      .pipe(finalize(() => this.isVerifyingMfa.set(false)))
      .subscribe((success) => {
        if (success) {
          this.navigateToReturnUrl();
        } else {
          this.messageService.add({
            severity: "error",
            detail: usingBackup
              ? "Invalid backup code. Please try again."
              : "Invalid authenticator code. Please try again.",
          });
        }
      });
  }

  toggleBackupCode(): void {
    this.useBackupCode.update((v) => !v);
    this.mfaForm.reset();
    this.backupCode = "";
  }

  cancelMfa(): void {
    this.mfaRequired.set(false);
    this.useBackupCode.set(false);
    this.mfaForm.reset();
    this.backupCode = "";
    this.authenticationService.cleanSession();
    this.loginForm.get("password")?.reset();
  }

  private navigateToReturnUrl(): void {
    const rawReturnUrl =
      this.route.snapshot.queryParamMap.get("returnUrl") ||
      CommonRoute.ADMIN_HOME;
    const returnUrl = this.returnUrlService.getSanitizedReturnUrl(rawReturnUrl);
    // Navigation can reject if the target's lazy chunk 404s on a stale build;
    // the router's navigation-error handler surfaces the reload prompt, so
    // swallow the rejection here to avoid an uncaught-promise console error.
    void this.router.navigate([returnUrl]).catch(() => undefined);
  }

  navigateToForgotPassword(): void {
    this.router.navigate([CommonRoute.REQUEST_PASSWORD_RESET]);
  }

  navigateToSignUp(): void {
    this.router.navigate([CommonRoute.SIGN_UP]);
  }

  navigateToLostAuthenticator(): void {
    this.router.navigate([CommonRoute.REQUEST_MFA_RESET]);
  }
}
